import {Injectable} from "@angular/core";
import {GridLayoutService} from "@irb-common/interfaces/grid-layout.service";
import {DeleteCellComponent} from "@irb-common/components/grid/renderers/delete.cell.component";
import {DeleteHeaderComponent} from "@irb-common/components/grid/renderers/delete.header.component";
import {UtilityService} from "@irb-common/services/utility.service";
import {formatDate} from "@angular/common";
import {ComHarvestTaxLossRebalanceSettingComponent} from "./harvest-tax-loss-rebalance-setting.component";
import {PortColFormatterComponent} from "@irb/irb-common/components/irebal-ag-grid-ent-avatar/portcolformatter.component";
import {validationPercentCellEditorComponent} from "../validationPercentCellEditor.component";
import {DropdownFilterRendererComponent} from "@irb/irb-common/components/grid/renderers/dropdown-filter.cell.component";
import {NumberTextBoxEditorComponent} from "@irb/irb-common/components/grid/editors/number-text-box-editor.component";
import {NumberTextBoxRendererComponent} from "@irb-common/components/grid/renderers/number-text-box.cell.component";
import {DropdownSearchRendererComponent} from "@irb/irb-common/components/grid/renderers/dropdown-search.cell.component";
import {IDropdownItem} from "@irb/irb-common/models/dropdown.model";
import {IrbAppEvents} from "@irb/irb-common/events";
import {AppService} from "@irb/app.service";
import {CheckboxCellComponent} from '@irb-common/components/grid/renderers/checkbox.cell.component';

@Injectable({
  providedIn: "root",
})
export class HarvestTaxLossRebalanceGridLayoutService implements GridLayoutService<ComHarvestTaxLossRebalanceSettingComponent> {
  sizeFor: any;

  constructor(private as: AppService, private util: UtilityService) {
    this.sizeFor = {
      startDate: 100,
      amount: 150,
      status: 80,
    };
  }

  getFrameworkComponents() {
    return {
      "DeleteHeaderComponent": DeleteHeaderComponent,
      "DeleteCellComponent": DeleteCellComponent,
      "cellRendererFormatter": PortColFormatterComponent,
      "validationPercentCellEditorComponent": validationPercentCellEditorComponent,
      "CheckboxCellComponent": CheckboxCellComponent,
      "NumberTextBoxEditorComponent": NumberTextBoxEditorComponent,
      "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent,
      "DropdownFilterRendererComponent": DropdownFilterRendererComponent,
      "DropdownSearchRendererComponent": DropdownSearchRendererComponent
    };
  }

  formatCreatedDate(params: any) {
    return formatDate(params.data.startDate, "MM/dd/yyyy h:mm a", "en-US", "EST");
  }

  cellStyleDollar(params: any) {
    if (params.value && params.value.includes("-")) {
      //mark police cells as red
      return {color: "#D30014", "text-align": "right"};
    } else {
      return {"text-align": "right"};
    }
  }

  getColumnDefs(parent: ComHarvestTaxLossRebalanceSettingComponent) {
    return [
      {
        headerName: "Delete All",
        headerComponent: "DeleteHeaderComponent",
        headerComponentParams: {
          message: "Remove all Entries?",
          // note: "Settings (such as maintained cash) will be deleted.",
          proceed: parent.deleteAll.bind(parent),
          cancel: parent.cancelDelete.bind(parent),
        },
        cellRenderer: "DeleteCellComponent",
        cellRendererParams: {
          message: "Remove this Entry?",
          // note: "Settings (such as maintained cash) will be deleted.",
          proceed: parent.delete.bind(parent),
          cancel: parent.cancelDelete.bind(parent),
        },
        resizable: false,
        minWidth: 25,
        maxWidth: 25,
        width: 25,
        pinned: "left",
      },
      {
        headerName: "Harvest",
        children: [
          {
            headerName: "Symbol",
            field: "harvestSecSymb",
            minWidth: this.sizeFor.startDate,
            flex: 1,
            sort: "asc",
          },
        ],
      },
      {
        headerName: "Sell If Loss >",
        headerGroupComponent: "customHeaderGroup",
        headerGroupComponentParams: {
          irbColInfo: this.sellIfLossInfo(),
        },
        children: [
          {
            headerName: "%",
            field: "percentageAmount",
            minWidth: this.sizeFor.amount,
            flex: 1,
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
              return {
                min: '0',
                max: 100,
                places: 2,
                autofocus: parent.showAutoFocus,
                isPercentage: true,
                invalidRangeMessage: 'Please enter a value between 0..100',
                onChange: (params: any, c: any) => {
                  if (params.data.percentageAmount != c.value && c.isValid) {
                    params.data.percentageAmount = parseFloat(c.value);
                    params.api.redrawRows({rowNodes: [params.node]});
                  }
                },
              };
            },
          },
          {
            headerName: "Amt",
            field: "dollarAmount",
            minWidth: this.sizeFor.startDate,
            flex: 1,
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
              return {
                min: '0',
                max: 9999999,
                places: 2,
                isCurrency: true,
                invalidRangeMessage: 'Number must be 0 or greater',
                onChange: (params: any, c: any) => {
                  if (params.data.dollarAmount != c.value && c.isValid) {
                    params.data.dollarAmount = parseFloat(c.value);
                    params.api.redrawRows({rowNodes: [params.node]});
                  }
                },
              };
            },
          },
        ],
      },
      {
        headerName: "Replace with",
        headerGroupComponent: "customHeaderGroup",
        headerGroupComponentParams: {
          irbColInfo: this.replaceWithInfo(),
        },
        children: [
          {
            headerName: "Symbol",
            field: "replaceSecSymb",
            suppressFiltersToolPanel: true,
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset", "apply"],
              newRowsAction: "keep",
              suppressSorting: true,
              cellHeight: 30,
              closeOnApply: true
            },
            cellClass: "dropdownSearchCell",
            cellRenderer: "DropdownSearchRendererComponent",
            cellRendererParams: (params: any) => {
              const addNewItemConfig = {
                button: 'Add new security',
                type: 'security',
                fromComp: 'HarvestTaxLossRebalanceGridLayoutService',
                rowIndex: params.rowIndex || params.node.rowIndex
            };
              return {
                placeholder: "None (leave in cash)",
                defaultValue: {name: params.data.replaceSecSymb, value: params.data.securityId},
                values: parent.securitiesTargetsGrid,
                addNewItemConfig: addNewItemConfig,
                onChange: (params: any, item: IDropdownItem) => {
                  parent.securityTargetData.forEach((element: any) => {
                    if (element.securitySymbol === item.name) {
                      params.data.replaceSecType = element.securityType;
                      parent.onPositionLevelChange();
                    }
                  });

                  if (parent.analysisType !== "Harvest tax losses" && parent.analysisType !== "Tax Loss Harvest") {
                    params.data.securityId = item.value;
                  }
                  params.data.replaceSecId = item.value;
                  params.data.replaceSecSymb = item.name;
                  params.api.redrawRows({rowNodes: [params.node]});

                  if (params.data.replaceSecSymb === params.data.v) {
                    this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                      severity: "warn",
                      summary: "",
                      detail: "Symbol " + params.data.securitySymbol + " can not be listed as a replacement buy security for " + params.data.securitySymbol,
                    });
                  } else {
                    parent.warnIfSymbolUsedAsEquityReplaceTarget(params.data.replaceSecSymb);
                  }
                },
                openNewItemFlow: (dataParam: any) => {
                  dataParam = Object.assign({}, addNewItemConfig, dataParam);
                  parent.openNewItemSecurityFlow(dataParam, true);
                },
                onClearSelection: (params: any) => {
                  if (parent.analysisType !== "Harvest tax losses" && parent.analysisType !== "Tax Loss Harvest") {
                    params.data.securityId = -2;
                  }
                  params.data.replaceSecId = -2;
                  params.data.replaceSecSymb = "";
                  params.data.replaceSecType = null;
                  //parent.enableswapexchange();
                  params.api.redrawRows({rowNodes: [params.node]});
                },
              };
            },
            minWidth: this.sizeFor.amount,
            flex: 1,
          },
          {
            headerName: "Swap/Exch",
            field: "isSwapExchange",
            minWidth: 100,
            flex: 1,
            cellRenderer: "CheckboxCellComponent",
            cellRendererParams: (params: any) => {
              const isEquityReplaceTarget = parent.checkIfSymbolUsedAsEquityReplaceTarget(params.data.replaceSecSymb);
               const swapExchNotAllowed = params.data.harvestSecType !== 'MUTUAL_FUND' || params.data.replaceSecType !== 'MUTUAL_FUND' || isEquityReplaceTarget;
               if (swapExchNotAllowed && params.data.isSwapExchange) {
                params.data.isSwapExchange = false;
              }

              return {
                value: params.data.isSwapExchange,
                isDisabled: swapExchNotAllowed,
                onChange: (newValue: boolean) => {
                  params.data.isSwapExchange = newValue;
                  parent.onPositionLevelChange();
                }
              }
            },
            suppressFiltersToolPanel: true,
            cellStyle: {"text-align": "center"}
          },
        ],
      },
    ];
  }

  getDisabledColumnDefs(parent: ComHarvestTaxLossRebalanceSettingComponent) {
    return [
      {
        headerName: "Harvest",
        children: [
          {
            headerName: "Symbol",
            field: "harvestSecSymb",
            minWidth: this.sizeFor.startDate,
            flex: 1,
            sort: "asc",
          },
        ],
      },
      {
        headerName: "Sell If Loss >",
        headerGroupComponent: "customHeaderGroup",
        headerGroupComponentParams: {
          irbColInfo: this.sellIfLossInfo(),
        },
        children: [
          {
            headerName: "%",
            field: "percentageAmount",
            minWidth: this.sizeFor.amount,
            flex: 1,
            cellEditor: "validationPercentCellEditorComponent",
            editable: false,
          },
          {
            headerName: "Amt",
            field: "dollarAmount",
            minWidth: this.sizeFor.startDate,
            flex: 1,
            cellEditor: "validationPercentCellEditorComponent",
            editable: false,
          },
        ],
      },
      {
        headerName: "Replace with",
        headerGroupComponent: "customHeaderGroup",
        headerGroupComponentParams: {
          irbColInfo: this.replaceWithInfo(),
        },
        children: [
          {
            headerName: "Symbol",
            field: "replaceSecSymb",
            suppressFiltersToolPanel: true,
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset", "apply"],
              newRowsAction: "keep",
              suppressSorting: true,
              cellHeight: 30,
              closeOnApply: true
            },
            editable: false,
            cellEditor: "agRichSelectCellEditor",
            cellEditorParams: {
              values: parent.securitiesTargetsGrid,
            },
            cellRenderer: "cellRendererFormatter",
            cellRendererParams: {type: "dropdown"},
            minWidth: this.sizeFor.amount,
            flex: 1,
          },
          {
            headerName: "Swap/Exch",
            field: "isSwapExchange",
            minWidth: 100,
            flex: 1,
            cellRenderer: "CheckboxCellComponent",
            cellRendererParams: (params: any) => {
              return {
                value: params.data.isSwapExchange,
                isDisabled: true
              }
            },
            suppressFiltersToolPanel: true,
            cellStyle: {"text-align": "center"}
          },
        ],
      },
    ];
  }

  sellIfLossInfo() {
    let msg = `iRebal will evaluate holdings in taxable accounts for each security that you list here. iRebal will first look at the % loss, either at the lot or position level based on your loss detection level setting below, and select only those (lots or positions) that exceed the % threshold you specify. Next, looking only at lots or positions that passed the % threshold check, iRebal will propose a sale only if the $ loss on the total sale of the security in the account will exceed the $ amount threshold you specify.`;
    return msg;
  }

  replaceWithInfo() {
    let msg = `You can tell iRebal to purchase a security in place of the security being sold, or you can choose to leave the proceeds of the sale in cash. You may select different replacement securities for each harvested security. In order to mitigate wash sale concerns, the security which is purchased to replace a harvested security will be considered as equivalent to the security sold if the portfolio is rebalanced within the next 30 days.`;
    return msg;
  }
}
